"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BookingItem = void 0;
const jsonUtils_1 = require("benjibooks-shared/utils/jsonUtils");
const isEqual_1 = __importDefault(require("lodash/isEqual"));
const nanoid_1 = require("nanoid");
const BookingDiscount_1 = require("./BookingDiscount");
const PriceModel_1 = require("./helper/PriceModel");
/**
 * An item inside a booking.
 */
class BookingItem {
    /**
     * Constructor.
     */
    constructor(config, configItem) {
        var _a;
        this.type = "";
        this.title = "";
        this.quantity = 0;
        this.unit = "";
        this.price = 0;
        /**
         * @see key
         */
        this._key = (0, nanoid_1.nanoid)();
        this.taxRate = config.defaultTaxRate;
        this.configItem = configItem;
        if (configItem) {
            // Initialize with config values.
            this.type = configItem.type;
            this.title = configItem.title;
            this.taxRate = configItem.taxRate;
        }
        this.quantity = (_a = configItem === null || configItem === void 0 ? void 0 : configItem.minQuantity) !== null && _a !== void 0 ? _a : 0;
    }
    /**
     * Item config.
     *
     * Note: The booking item must be able to work without the config because old
     * bookings might be opened without a corresponding config.
     */
    get configItem() {
        return this._configItem;
    }
    /**
     * Set the item config.
     *
     * If no config is provided, the item is set to `customized`.
     */
    set configItem(configItem) {
        this._configItem = configItem;
        if (!configItem) {
            // No config, set `customized` to true.
            this.customized = true;
        }
    }
    /**
     * A key for this booking item.
     *
     * The key is a random generated id that is NOT saved in the db and thus is
     * only consistent as long as the item exists in memory but not if the item
     * is reloaded from the db.
     */
    get key() {
        return this._key;
    }
    /**
     * Ensures that the type and title of this item still match the config item. If not, it is
     * replaced by a matching item config. If none is found inside the specified booking, it
     * is set to undefined.
     *
     * Note: This will not change the price. If the config change would also change the price we
     * set this item to `customized` to prevent unwanted price changes.
     */
    updateConfigItem(booking) {
        const newConfigItem = booking.findItemConfig(this.type, this.title);
        this.configItem = newConfigItem;
        const newPrice = this.getConfigPrice(booking.isFullDay());
        if (this.price !== newPrice) {
            // New calculated price does not match the price before changing the config.
            // Set this item to customized to prevent unwanted price changes.
            this.customized = true;
        }
    }
    /**
     * Constructs an object of this class from the specified JSON-compatible dictionary object.
     */
    static fromJSON(json, config, booking) {
        let newItemConfig = undefined;
        if (typeof json.type === "string" && typeof json.title === "string") {
            newItemConfig = booking.findItemConfig(json.type, json.title);
        }
        let obj = new BookingItem(config, newItemConfig);
        Object.assign(obj, json);
        // Overwrite fields that need converting.
        if ((0, jsonUtils_1.isJSONObject)(json.discount)) {
            obj.discount = BookingDiscount_1.BookingDiscount.fromJSON(json.discount);
        }
        obj.updateConfigItem(booking);
        return obj;
    }
    equalDiscount(otherDiscount) {
        if ((!this.discount || this.discount.isEmpty()) &&
            (!otherDiscount || otherDiscount.isEmpty())) {
            // Both empty or undefined.
            return true;
        }
        else {
            return (0, isEqual_1.default)(this.discount, otherDiscount);
        }
    }
    /**
     * Converts this object to JSON-compatible dictionary object.
     * All fields from `this` will be in the returned object except the `undefined` values.
     */
    toJSON() {
        const json = (0, jsonUtils_1.toJSONWithFilters)(this, {
            filterUndefined: true,
            filterFalse: true,
            filterUnderscore: true, // _configItem, _key
        });
        // Overwrite fields that need converting.
        if (this.discount) {
            json.discount = this.discount.toJSON();
        }
        return json;
    }
    /** Returns the price after deducting the discount. */
    calcPriceAfterDiscount() {
        if (this.discount) {
            return this.price - this.discount.calc(this.price);
        }
        else {
            return this.price;
        }
    }
    /**
     * Calculates the price with the config of this item.
     * Returns `undefined` if no config is present for this item.
     */
    getConfigPrice(fullDay) {
        if (this.configItem === undefined) {
            return undefined;
        }
        else {
            return (0, PriceModel_1.priceModelCalc)(this.configItem.priceModel, this.quantity, fullDay);
        }
    }
    /**
     * Updates the price and the unit depending on the quantity.
     *
     * Note: If there is no matching config or it is marked as customized,
     * this method will have no effect.
     */
    updatePriceAndUnit(fullDay) {
        var _a;
        if (this.configItem && !this.customized) {
            // Update unit.
            this.unit =
                this.quantity === 1
                    ? this.configItem.units.singular
                    : this.configItem.units.plural;
            // Update price.
            this.price = (_a = this.getConfigPrice(fullDay)) !== null && _a !== void 0 ? _a : 0;
        }
    }
    /**
     * Checks if the provided discount code can be applied to this item.
     * If a corresponding discount is available it is set on this item.
     *
     * Note: If there is no matching config this method will have no effect.
     */
    useDiscountCode(discountCode) {
        if (this.configItem) {
            // Try to find a matching discount.
            // If no match is found, discount is set to undefined.
            const configDiscountMatch = this.configItem.findDiscount(discountCode);
            if (configDiscountMatch) {
                this.discount = BookingDiscount_1.BookingDiscount.fromConfigDiscount(configDiscountMatch);
            }
            else {
                this.discount = undefined;
            }
        }
    }
    /**
     * Resets this item to the item in the config.
     *
     * The price and unit are updated to match the quantity.
     */
    reset(fullDay) {
        if (this.configItem) {
            this.customized = false;
            this.updatePriceAndUnit(fullDay);
        }
    }
}
exports.BookingItem = BookingItem;
